import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  CreateDictionariesRouteModel,
  DictionariesRouteModel,
  UpdateDictionariesRouteModel,
} from "../../../viewModels/Dictionaries/DictionariesRouteModel";

import LocationModel from "../../../viewModels/Location/LocationModel";
import { DigitalLogistCarrierBasicInfoModel } from "../../../viewModels/Carriers/DigitalLogistCarrierModel";
import { ROUTES_DICTIONARY_PAGE_TAB_KEYS } from "./constants";
import SortObject from "../../../types/SortObject";
import { RouteFilterInputModel } from "./components/FiltersDrawer";

const initialState: {
  loading: boolean;
  loadingData: boolean;
  activeApplicationsTabId: ROUTES_DICTIONARY_PAGE_TAB_KEYS;
  routes: Array<DictionariesRouteModel>;

  locations: Array<LocationModel>;

  carriers: Array<DigitalLogistCarrierBasicInfoModel>;

  isFiltersDrawerVisible: boolean;
  isFiltersDrawerLoading: boolean;

  total: number;
  pageSize: number;
  page: number;
  sort: SortObject;
  searchFilterValue: string;

  routeIdToEdit: number;
  isEditRouteDrawerOpen: boolean;
  isEditRouteDrawerLoading: boolean;

  filters: RouteFilterInputModel;

  table: { isAllSelect: boolean; isNotAllSelect: boolean; selectedRoutesIds: number[]; exceptionRoutesIds: number[] };
} = {
  loading: true,
  loadingData: true,
  activeApplicationsTabId: ROUTES_DICTIONARY_PAGE_TAB_KEYS.ALL,

  routes: undefined!,

  locations: undefined!,

  carriers: undefined!,

  isFiltersDrawerVisible: false,
  isFiltersDrawerLoading: false,

  total: undefined!,
  pageSize: 10,
  page: 1,

  sort: undefined!,
  searchFilterValue: "",

  routeIdToEdit: undefined!,
  isEditRouteDrawerLoading: false,
  isEditRouteDrawerOpen: false,

  filters: {
    fromLocation: undefined!,
    toLocation: undefined!,
    fromDate: undefined!,
    toDate: undefined!,
    isExpired: undefined!,
    isDanger: undefined!,
    isOnlyHighWayRoutes: undefined!,
    transportCharacteristicId: undefined!,
    transportationTypeId: undefined!,
    carrierId: undefined!,
  },

  table: {
    isAllSelect: false,
    isNotAllSelect: false,
    selectedRoutesIds: [],
    exceptionRoutesIds: [],
  },
};

const dictionariesRoutesSlice = createSlice({
  name: "dictionariesRoutes",
  initialState,
  reducers: {
    exportRoutes() {},
    resetTableState(state) {
      state.table = initialState.table;
    },
    setIsAllSelect(state, action: PayloadAction<boolean>) {
      state.table.isAllSelect = action.payload;
    },
    setIsNotAllSelect(state, action: PayloadAction<boolean>) {
      state.table.isNotAllSelect = action.payload;
    },
    setSelectedRoutesIds(state, action: PayloadAction<number[]>) {
      state.table.selectedRoutesIds = action.payload;
    },
    setExceptionRoutesIds(state, action: PayloadAction<number[]>) {
      state.table.exceptionRoutesIds = action.payload;
    },
    setLoadDataStatus(state, action: PayloadAction<boolean>) {
      state.loadingData = action.payload;
    },
    changeActiveApplicationsTabId(state, action: PayloadAction<ROUTES_DICTIONARY_PAGE_TAB_KEYS>) {
      state.loadingData = true;
      state.page = 1;
      state.activeApplicationsTabId = action.payload;
    },
    loadRoutes(state) {
      state.loadingData = true;
      state.page = 1;
    },
    setLoadedRoutes(state, action: PayloadAction<{ routes: Array<DictionariesRouteModel>; total: number }>) {
      const { routes, total } = action.payload;
      state.loading = false;
      state.loadingData = false;
      state.routes = routes;
      state.total = total;
      state.isFiltersDrawerVisible = false;
      state.isFiltersDrawerLoading = false;
    },
    loadRoutesReject(state) {
      state.loading = false;
      state.loadingData = false;
    },
    loadLocationsSuccess(state, action: PayloadAction<Array<LocationModel>>) {
      state.locations = action.payload;
    },
    changeTable(state, action: PayloadAction<{ page: number; pageSize: number; sort: SortObject }>) {
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
      state.sort = action.payload.sort;
      state.loadingData = true;
    },
    openFiltersDrawer(state) {
      state.isFiltersDrawerVisible = true;
    },
    closeFiltersDrawer(state) {
      if (!state.isFiltersDrawerLoading) {
        state.isFiltersDrawerVisible = false;
      }
    },
    filterRoutes(state, action: PayloadAction<any>) {
      state.filters = action.payload;
      state.loadingData = true;
      state.isFiltersDrawerLoading = true;
      state.page = 1;
    },
    clearFilter(state, action: PayloadAction<keyof any>) {
      state.loadingData = true;
      state.filters = { ...state.filters, [action.payload]: undefined! };
    },
    openEditRouteDrawer(state, action: PayloadAction<number | undefined>) {
      state.routeIdToEdit = action.payload!;
      state.isEditRouteDrawerOpen = true;
    },
    closeEditRouteDrawer(state) {
      if (!state.isEditRouteDrawerLoading) {
        state.routeIdToEdit = undefined!;
        state.isEditRouteDrawerOpen = false;
      }
    },

    createRoute(state, _action: PayloadAction<CreateDictionariesRouteModel>) {
      state.isEditRouteDrawerLoading = true;
    },
    createRouteSuccess(state) {
      state.isEditRouteDrawerLoading = false;
      state.isEditRouteDrawerOpen = false;
    },
    createRouteReject(state) {
      state.isFiltersDrawerLoading = false;
      state.isEditRouteDrawerOpen = false;
    },

    updateRoute(state, _action: PayloadAction<UpdateDictionariesRouteModel>) {
      state.isEditRouteDrawerLoading = true;
    },
    updateRouteSuccess(state) {
      state.isEditRouteDrawerLoading = false;
      state.isEditRouteDrawerOpen = false;
    },
    updateRouteReject(state) {
      state.isEditRouteDrawerLoading = false;
      state.isEditRouteDrawerOpen = false;
    },

    loadCarriers() {},
    loadCarriersSuccess(state, action: PayloadAction<Array<DigitalLogistCarrierBasicInfoModel>>) {
      state.carriers = action.payload;
    },

    markForDeletion(state) {
      state.loadingData = true;
    },

    unmarkForDeletion(state) {
      state.loadingData = true;
    },
  },
});

export default dictionariesRoutesSlice;
